import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import MobileShell, { LocationSelect, useMobileLocation, Card, Empty, Skeleton } from "./MobileShell";

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function DayPasses({ location, date }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get("/daypasses", { params: { date, location } }).then(({ data }) => setState(data)).catch((e) => setState({ passes: [], error: e.userMessage }));
  }, [date, location]);
  useEffect(() => { load(); }, [load]);
  const act = async (p, verb) => {
    setBusy(p.id);
    try { await api.post(`/daypasses/${p.id}/${verb}`); toast.success(`${verb === "checkin" ? "Checked in" : "Checked out"} ${p.guest_name || "guest"}`); load(); }
    catch (e) { toast.error("Didn't work", { description: e.userMessage }); }
    finally { setBusy(null); }
  };
  if (!state) return <Skeleton />;
  return (
    <div className="space-y-2" data-testid="m-daypasses">
      <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary px-1">Day passes</p>
      {state.error ? <p className="text-sm text-brand-neg">{state.error}</p> : !state.passes.length ? <Card className="p-4 text-sm text-brand-tertiary">No day passes today.</Card> : (
        <Card className="divide-y divide-brand-border">
          {state.passes.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`m-pass-${p.id}`}>
              <span className="flex-1 min-w-0">
                <span className="block text-base truncate">{p.guest_name || "Guest"}</span>
                <span className="block text-xs text-brand-tertiary truncate">{[p.company, p.checked_in ? "Checked in" : "Not here yet"].filter(Boolean).join(" · ")}</span>
              </span>
              <button disabled={busy === p.id} onClick={() => act(p, p.checked_in ? "checkout" : "checkin")} data-testid={`m-pass-${p.checked_in ? "out" : "in"}-${p.id}`}
                className={`min-h-[44px] px-4 rounded-xl text-sm font-medium active:scale-95 transition-transform disabled:opacity-50 ${p.checked_in ? "border border-brand-border" : "bg-brand-primary text-brand-page"}`}>
                {p.checked_in ? "Check out" : "Check in"}
              </button>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function Rooms({ location, date }) {
  const [rooms, setRooms] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/bookings/events", { params: { date, location } }).then(({ data }) => setRooms(data.rooms || [])).catch((e) => { setRooms([]); setError(e.userMessage); });
  }, [date, location]);
  if (!rooms) return <Skeleton />;
  const now = new Date();
  return (
    <div className="space-y-2" data-testid="m-rooms">
      <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary px-1">Room bookings</p>
      {error && <p className="text-sm text-brand-neg">{error}</p>}
      {rooms.map((r) => (
        <Card key={r.id} className="p-4 space-y-2" data-testid={`m-room-${r.id}`}>
          <p className="text-base font-medium">{r.room} <span className="text-xs text-brand-tertiary font-normal">· {r.capacity} seats</span></p>
          {!r.events.length ? <p className="text-sm text-brand-tertiary">Free all day</p> : r.events.map((e) => {
            const live = new Date(e.start) <= now && now < new Date(e.end);
            return (
              <div key={e.id} className={`flex gap-3 text-sm rounded-lg px-3 py-2 ${live ? "bg-brand-gold/10 ring-1 ring-brand-gold/40" : "bg-brand-hover/60"}`}>
                <span className="w-[7.5rem] shrink-0 text-brand-muted text-xs leading-5 whitespace-nowrap">{time(e.start)} – {time(e.end)}</span>
                <span className="flex-1 min-w-0 truncate">{e.company || e.summary}{e.booked_by ? ` · ${e.booked_by}` : ""}</span>
                {live && <span className="text-[10px] uppercase tracking-wider text-brand-gold self-center">Now</span>}
              </div>
            );
          })}
        </Card>
      ))}
      {!rooms.length && !error && <Card className="p-4 text-sm text-brand-tertiary">No rooms set up for this location.</Card>}
    </div>
  );
}

export default function MToday() {
  const { location } = useMobileLocation();
  const date = todayStr();
  return (
    <MobileShell title="Today">
      <LocationSelect allowAll={false} />
      {!location ? <Empty data-testid="m-today-pick">Pick a location to see today's rooms and day passes.</Empty> : (
        <>
          <DayPasses location={location} date={date} />
          <Rooms location={location} date={date} />
        </>
      )}
    </MobileShell>
  );
}
